import {
  formatCurrency,
  formatDateOnly,
  formatDateTime,
  formatFriendlyDate,
  formatMonth,
  formatPensionFormula,
  getMonthKey,
  isCurrentOrUpcomingMonthKey,
} from '../config/resources/utils'
import {
  getFixedAmountFromConfig,
  getImmPercentageFromConfig,
  getUtmQuantityFromConfig,
  METHOD,
  normalizePensionMethod,
} from '../config/settings/constants'
import { supabaseGestionPension as supabase } from '../config/settings/supabase-client'
import { getLogger } from '../core/debuggers'
import { mapUtmFromDb, mapUtmToDb } from '../mappers/utm.mapper'
import { fetchLatestImm, fetchPensionConfig } from './onboarding'

const logger = getLogger('pension.js')

const PAGE_SIZE = 1000
const page = 0 // página que quieras consultar

const pickActivePensionConfig = (configs) => {
  if (!configs) return null
  const list = Array.isArray(configs) ? configs : [configs]
  return list.find((config) => config?.is_active) || list[0] || null
}

/**
 * Factor for the calculation UI — always from active pension_configs.
 */
const resolvePensionFactor = (profile) => {
  const config = pickActivePensionConfig(profile?.pension_configs)
  if (!config) return null

  const method = normalizePensionMethod(config.method)
  if (method === METHOD.UTM) {
    const quantity = getUtmQuantityFromConfig(config)
    return quantity === '' || quantity == null ? null : quantity
  }
  if (method === METHOD.IMM) {
    const pct = getImmPercentageFromConfig(config)
    return pct === '' || pct == null ? null : pct
  }
  if (method === METHOD.FIXED_AMOUNT) {
    const amount = getFixedAmountFromConfig(config)
    return amount === '' || amount == null ? null : formatCurrency(amount)
  }

  return null
}

/**
 * Adds a new UTM record.
 */
export async function createUtm(utm) {
  const dbPayload = mapUtmToDb(utm)

  const { data, error } = await supabase
    .from('utm_history')
    .insert(dbPayload)
    .select()
    .single()

  if (error) {
    logger.error('Supabase error:', error)
    throw error
  }

  return mapUtmFromDb(data)
}

/**
 * Gets the list of utm's.
 */
export async function getUtms() {
  const { data, error } = await supabase
    .from('utm_history')
    .select('*')
    .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)
    .order('retrieved_at', { ascending: false })

  if (error) {
    console.error('Supabase error:', error)
    throw error
  }

  return data.map(mapUtmFromDb)
}

/**
 * Gets the list of users with active pension config.
 */
export async function getUsers() {
  const { data, error } = await supabase
    .from('profiles')
    .select(`
      id,
      full_name,
      email,
      whatsapp,
      avatar_url,
      status,
      onboarding_status,
      created_at,
      updated_at,
      deleted_at,
      pension_configs (
        method,
        amount,
        percentage,
        payment_day,
        is_active,
        setup_completed,
        created_at,
        updated_at
      ),
      acl_user_roles (
        is_active,
        created_at,
        acl_roles (
          name
        )
      )
    `)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Supabase error:', error)
    throw error
  }

  const transformed = data.map((item) => {
    const config = pickActivePensionConfig(item.pension_configs)
    const method = config ? normalizePensionMethod(config.method) : null
    const factorValue = resolvePensionFactor(item)
    const factorLabel =
      factorValue == null
        ? null
        : method === METHOD.FIXED_AMOUNT
          ? factorValue
          : method === METHOD.IMM
            ? `${factorValue}% IMM`
            : `${factorValue} UTM`
    const pensionValueRaw =
      method === METHOD.FIXED_AMOUNT
        ? config?.amount ?? null
        : config?.percentage ?? null

    return {
      id: item.id,
      name: item.full_name,
      email: item.email,
      whatsapp: item.whatsapp || null,
      avatarUrl: item.avatar_url || null,
      status: item.status,
      onboardingStatus: item.onboarding_status || 'pending',
      setupCompleted:
        item.onboarding_status === 'completed' ||
        Boolean(config?.setup_completed),
      pensionMethod: method,
      pensionFactor: factorValue,
      pensionFactorLabel: factorLabel,
      pensionValueRaw,
      paymentDay: config?.payment_day ?? null,
      configCreatedAt: config?.created_at || null,
      configUpdatedAt: config?.updated_at || null,
      roles: (item.acl_user_roles || [])
        .filter((assignment) => assignment.is_active && assignment.acl_roles?.name)
        .map((assignment) => assignment.acl_roles.name),
      createdAt: formatDateTime(item.created_at),
      createdAtRaw: item.created_at || null,
      updatedAt: formatDateTime(item.updated_at),
      deletedAt: formatDateTime(item.deleted_at),
    }
  })

  return transformed
}

/**
 * Pension calculations for a single user.
 */
export async function getPensionCalculationsByUserId(userId) {
  if (!userId) return []

  const [{ data, error }, config] = await Promise.all([
    supabase
      .from('pension_calculations')
      .select(`
        id,
        user_id,
        calculated_amount,
        calculation_date,
        status,
        created_at,
        updated_at,
        payment_at,
        method,
        percentage,
        amount,
        reference_kind,
        reference_value,
        reference_source,
        utm_history (
          id,
          value,
          target_month,
          source
        )
      `)
      .eq('user_id', userId)
      .order('calculation_date', { ascending: false }),
    fetchPensionConfig(userId).catch(() => null),
  ])

  if (error) {
    console.error('Supabase error:', error)
    throw error
  }

  const mapped = (data || []).map((item) => {
    const snapshotMethod = normalizePensionMethod(
      item.method || config?.method || METHOD.UTM
    )
    const refValue = item.reference_value ?? item.utm_history?.value
    const refKind = (
      item.reference_kind ||
      (snapshotMethod === METHOD.IMM
        ? 'IMM'
        : snapshotMethod === METHOD.FIXED_AMOUNT
          ? 'FIXED'
          : 'UTM')
    ).toUpperCase()
    const refFormatted = formatCurrency(refValue)
    const referenceLabel =
      refKind === 'IMM'
        ? `IMM ${refFormatted}`
        : refKind === 'FIXED'
          ? 'Monto fijo'
          : `UTM ${refFormatted}`
    const factor =
      snapshotMethod === METHOD.FIXED_AMOUNT
        ? item.amount
        : item.percentage
    const formula = formatPensionFormula({
      method: snapshotMethod,
      percentage: item.percentage,
      amount: item.amount,
      referenceValue: refValue,
      calculatedAmount: item.calculated_amount,
    })

    return {
      id: item.id,
      userId: item.user_id || '',
      calculatedAmount: formatCurrency(item.calculated_amount),
      calculationDate: formatDateOnly(item.calculation_date),
      calculationDateRaw: item.calculation_date || null,
      status: item.status,
      createdAt: formatDateTime(item.created_at),
      createdAtRaw: item.created_at || null,
      updatedAt: formatDateTime(item.updated_at),
      paymentAt: formatFriendlyDate(item.payment_at),
      pensionMethod: snapshotMethod,
      referenceLabel,
      referenceSource: item.reference_source || item.utm_history?.source || null,
      formula,
      factor,
      utmValue: formatCurrency(item.utm_history?.value),
      month: item.utm_history?.target_month || null,
      targetMonth: formatMonth(item.utm_history?.target_month),
    }
  })

  // Newest → oldest by year/month (target_month), then calculation_date.
  return mapped.sort((a, b) => {
    const aKey = a.month || a.calculationDateRaw || ''
    const bKey = b.month || b.calculationDateRaw || ''
    if (aKey === bKey) return 0
    return aKey > bKey ? -1 : 1
  })
}

/**
 * Gets the list of pension calculation.
 */
export async function getPensionCalculations() {
  const [{ data, error }, latestImm] = await Promise.all([
    supabase
      .from('pension_calculations')
      .select(`
        id,
        user_id,
        calculated_amount,
        calculation_date,
        status,
        created_at,
        updated_at,
        payment_at,
        profiles (
          id,
          full_name,
          pension_configs (
            method,
            amount,
            percentage,
            is_active
          )
        ),
        utm_history (
          id,
          value,
          target_month
        )
      `)
      .order('created_at', { ascending: false }),
    fetchLatestImm().catch(() => null),
  ])

  if (error) {
    console.error('Supabase error:', error)
    throw error
  }

  const immValueFormatted = latestImm?.value != null
    ? formatCurrency(latestImm.value)
    : null

  const transformed = data.map((item) => {
    const config = pickActivePensionConfig(item.profiles?.pension_configs)
    const method = config ? normalizePensionMethod(config.method) : null
    const factorValue = resolvePensionFactor(item.profiles)
    const utmValueFormatted = formatCurrency(item.utm_history?.value)

    let referenceLabel = null
    let factorLabel = null

    if (method === METHOD.IMM) {
      referenceLabel = immValueFormatted ? `IMM ${immValueFormatted}` : 'IMM —'
      factorLabel =
        factorValue != null && factorValue !== ''
          ? `${factorValue}%`
          : '—%'
    } else if (method === METHOD.FIXED_AMOUNT) {
      referenceLabel = 'Monto fijo'
      factorLabel =
        factorValue != null && factorValue !== '' ? String(factorValue) : '—'
    } else {
      // UTM (default)
      referenceLabel = item.utm_history?.value != null
        ? `UTM ${utmValueFormatted}`
        : 'UTM —'
      factorLabel =
        factorValue != null && factorValue !== ''
          ? `${factorValue} UTM`
          : '— UTM'
    }

    return {
      id: item.id,
      userId: item.user_id || item.profiles?.id || '',
      calculatedAmount: formatCurrency(item.calculated_amount),
      amount:
        item.calculated_amount != null ? Number(item.calculated_amount) : null,
      calculationDate: formatDateOnly(item.calculation_date),
      status: item.status,
      createdAt: formatDateTime(item.created_at),
      updatedAt: formatDateTime(item.updated_at),
      paymentAt: formatFriendlyDate(item.payment_at),
      userName: item.profiles?.full_name || '',
      pensionMethod: method,
      pensionFactor: factorValue ?? '—',
      referenceLabel,
      factorLabel,
      utmValue: utmValueFormatted,
      immValue: immValueFormatted,
      month: item.utm_history?.target_month,
      targetMonth: formatMonth(item.utm_history?.target_month),
    }
  })

  return transformed
}

/**
 * Delete a UTM.
 */
export async function deleteUtm({ id }) {
  const { error } = await supabase
    .from('utm_history')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting UTM:', error)
    throw error
  }

  return true
}

/**
 * Delete a pension calculation.
 */
export async function deletePensionCalculation({ id }) {
  const { error } = await supabase
    .from('pension_calculations')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('Error deleting a pension calculation:', error)
    throw error
  }

  return true
}

/**
 * Update a pension calculation.
 */
export async function updatePensionCalculation({ id, updatedObject }) {
  const body = {
    calculated_amount: updatedObject.calculatedAmount,
    status: updatedObject.status,
    calculation_date: updatedObject.calculationDate,
    updated_at: updatedObject.updatedAt
  }

  logger.debug(body)

  // TODO: change to update function, figure out how pass the body into the request.
  const { error } = await supabase
    .from('pension_calculations')
    .delete()
    .eq('id', id)

  if (error) {
    logger.error(error);
    throw error
  }

  return true
}

/**
 * Update pension calculation status.
 */
export const updatePensionCalculationStatusAsync = async ({ id, status, paymentAt }) => {
  if (!id) throw new Error('id is required')
  if (!status) throw new Error('status is required')

  const updatePayload = {
    status,
    payment_at: status === 'payed' ? (paymentAt ?? new Date()) : null,
  };

  const { data, error } = await supabase
    .from('pension_calculations')
    .update(updatePayload)
    .eq('id', id)
    .select()
    .maybeSingle();

  if (error) {
    logger.error('updatePensionCalculationStatusAsync failed', { id, status, paymentAt, error });
    throw error
  }

  if (!data) {
    throw new Error('Pension calculation not found or not allowed');
  }

  return data;
}

/**
 * Update user status.
 */
export const updateUserStatusAsync = async ({ id, status }) => {
  const { data, error } = await supabase
    .from('profiles')
    .update({ status })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    if (String(error.message || '').includes('cannot_deactivate_self')) {
      throw new Error('No puedes desactivar tu propia cuenta.')
    }
    console.error('Supabase error:', error)
    throw error
  }

  return data;
}

/**
 * Active roles in the ACL catalog.
 */
export async function getRolesCatalog() {
  const { data, error } = await supabase
    .from('acl_roles')
    .select('id, name, description, is_active')
    .eq('is_active', true)
    .order('name')

  if (error) {
    console.error('Supabase error:', error)
    throw error
  }

  return data || []
}

/**
 * Role assignments for a given user (admin can read others after RLS).
 */
export async function getUserRolesByUserId(userId) {
  if (!userId) return []

  const { data, error } = await supabase
    .from('acl_user_roles')
    .select(`
      id,
      is_active,
      role:acl_roles (
        id,
        name
      )
    `)
    .eq('user_id', userId)

  if (error) {
    console.error('Supabase error:', error)
    throw error
  }

  return (data || [])
    .filter((row) => row.is_active && row.role?.name)
    .map((row) => ({
      assignmentId: row.id,
      id: row.role.id,
      name: row.role.name,
    }))
}

/**
 * Assign or deactivate a role for a user.
 */
export async function setUserRole({ userId, roleId, active }) {
  const { data: existing, error: findError } = await supabase
    .from('acl_user_roles')
    .select('id')
    .eq('user_id', userId)
    .eq('role_id', roleId)
    .maybeSingle()

  if (findError) {
    console.error('Supabase error:', findError)
    throw findError
  }

  if (existing) {
    const { error } = await supabase
      .from('acl_user_roles')
      .update({ is_active: active })
      .eq('id', existing.id)

    if (error) {
      console.error('Supabase error:', error)
      throw error
    }

    return { userId, roleId, active }
  }

  if (!active) {
    return { userId, roleId, active }
  }

  const { error } = await supabase.from('acl_user_roles').insert({
    user_id: userId,
    role_id: roleId,
    is_active: true,
  })

  if (error) {
    console.error('Supabase error:', error)
    throw error
  }

  return { userId, roleId, active }
}

/**
 * Gets the list of logs.
 */
export async function getLogs({ context }) {
  const { data, error } = await supabase
    .from('logs')
    .select('*')
    .eq('context', context)
    .order('created_at', { ascending: false })
    .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

  if (error) {
    console.error('Supabase error:', error);
    throw error;
  }

  return data.map(item => ({
    id: item.id,
    createdAt: formatDateTime(item.created_at),
    level: item.level,
    context: item.context,
    message: item.message,
    metadata: item.metadata
  }));
}

const monthKeyUtc = (value) => {
  if (!value) return null
  const raw = typeof value === 'string' ? value.slice(0, 10) : null
  if (raw && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw.slice(0, 7)
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
}

/**
 * Latest UTM row used by the sender (most recent target_month).
 * @returns {Promise<{ id: string|number, targetMonth: string }|null>}
 */
export async function getLatestUtm() {
  const { data, error } = await supabase
    .from('utm_history')
    .select('id, target_month, value')
    .order('target_month', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) {
    logger.error('getLatestUtm error:', error)
    throw error
  }

  if (!data) return null

  return {
    id: data.id,
    targetMonth: data.target_month,
    value: data.value,
  }
}

/**
 * Latest pension calculation for a user (same ordering as dashboard RPC).
 */
export async function getLatestPensionCalculation(userId) {
  if (!userId) return null

  const { data, error } = await supabase
    .from('pension_calculations')
    .select(`
      id,
      status,
      payment_at,
      calculated_amount,
      utm_history (
        target_month
      )
    `)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (error) {
    logger.error('getLatestPensionCalculation failed', { userId, error })
    throw error
  }

  if (!data) return null

  const targetMonthRaw = data.utm_history?.target_month ?? null

  return {
    id: data.id,
    status: data.status,
    paymentAt: formatFriendlyDate(data.payment_at),
    targetMonth: formatMonth(targetMonthRaw),
    targetMonthRaw,
    targetMonthKey: getMonthKey(targetMonthRaw),
    amount: data.calculated_amount,
  }
}

/**
 * Whether the user already has a pension_calculations row for the latest UTM month.
 */
export async function hasPensionCalculationForLatestUtm(userId) {
  if (!userId) return false

  const latestUtm = await getLatestUtm()
  if (!latestUtm?.targetMonth) return false

  const targetKey = monthKeyUtc(latestUtm.targetMonth)
  const calculations = await getPensionCalculationsByUserId(userId)

  return calculations.some(
    (row) => monthKeyUtc(row.month) === targetKey
  )
}

/**
 * True when a pending current/upcoming snapshot still has a different factor/method than config.
 */
export function pendingSnapshotMatchesConfig(row, config) {
  if (!row || !config) return false
  const method = normalizePensionMethod(config.method)
  if (normalizePensionMethod(row.pensionMethod) !== method) return false
  if (row.factor == null || row.factor === '') return false

  const configFactor =
    method === METHOD.UTM
      ? getUtmQuantityFromConfig(config)
      : method === METHOD.IMM
        ? getImmPercentageFromConfig(config)
        : getFixedAmountFromConfig(config)

  return Number(row.factor) === Number(configFactor)
}

/**
 * Whether any unpaid snapshot for this or a future month still uses an old factor.
 */
export async function hasStalePendingPensionSnapshot(userId, config) {
  if (!userId || !config) return false

  const calculations = await getPensionCalculationsByUserId(userId)
  return calculations.some((row) => {
    if (row.status !== 'pending') return false
    const monthKey = getMonthKey(row.month)
    if (!isCurrentOrUpcomingMonthKey(monthKey)) return false
    return !pendingSnapshotMatchesConfig(row, config)
  })
}

